import React from "react";
import { Check, ExternalLink, Info } from "lucide-react";

/**
 * Two states, per the brand system.
 * verified      — links to the laboratory's own domain, in a new tab. Never ours.
 * self-described — honest about the absence of a third-party report.
 */
export function VerificationBadge({ certificate, size = "md", className = "", testId = "verification-badge" }) {
  const small = size === "sm";
  if (certificate && certificate.is_third_party) {
    return (
      <a
        href={certificate.verification_url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testId}
        className={`ui inline-flex items-center gap-2 border border-verify/60 bg-verify/[0.06] text-verify transition-colors hover:bg-verify/[0.12] ${
          small ? "px-2 py-1 text-[11px]" : "px-3 py-1.5 text-xs"
        } ${className}`}
      >
        <Check className={small ? "h-3 w-3" : "h-3.5 w-3.5"} aria-hidden="true" />
        <span className="num font-medium">
          {certificate.lab} report {certificate.report_number}
        </span>
        <span className="hidden sm:inline">— verify on {new URL(certificate.verification_url).hostname.replace("www.", "")}</span>
        <ExternalLink className="h-3 w-3" aria-hidden="true" />
      </a>
    );
  }
  return (
    <span
      data-testid={`${testId}-self-described`}
      className={`ui inline-flex items-center gap-2 border border-rule text-dim ${
        small ? "px-2 py-1 text-[11px]" : "px-3 py-1.5 text-xs"
      } ${className}`}
    >
      <Info className={small ? "h-3 w-3" : "h-3.5 w-3.5"} aria-hidden="true" />
      No third-party report — here is exactly what we can and cannot tell you
    </span>
  );
}

export function SelfDescribedBlock({ product }) {
  return (
    <div className="card-s border-l-2 border-l-gold p-5" data-testid="self-described-block">
      <h3 className="text-lg">What we can and cannot tell you about this item</h3>
      <div className="ui mt-3 space-y-3 text-sm text-dim">
        <p>
          <strong className="text-ink">What we can tell you.</strong> We bought this from a named supplier,
          photographed the actual item you are being sent, and measured it ourselves. The dimensions, the
          weight and the origin as stated to us are on this page.
        </p>
        <p>
          <strong className="text-ink">What we cannot tell you.</strong> There is no third-party laboratory
          report for this item, because a report costs more than the item is worth and any laboratory
          that would issue one cheaply is a laboratory we would refuse. We are not going to print a
          certificate of our own and call it certification.
        </p>
        <p>
          Being visibly honest about a ₹1,200 bead that has no meaningful certificate is what makes the
          ₹1,52,500 sapphire's certificate believable. The 30-day return and the independent-testing
          guarantee still apply to this item in full.
        </p>
      </div>
    </div>
  );
}
